import {selectDefined} from '@benchling/js-utils';
import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as EventConstants from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import * as ToastActions from 'nav/toasts/ToastActions';
import {DEFAULT_DURATION, RenderedToast, ToastId, ToastType} from 'nav/toasts/ToastConstants';
import {FOREVER_DURATION} from 'notifications';

class ToastStore extends Store {
  toasts!: Array<RenderedToast>;

  resetState(): void {
    this.toasts = [];
  }
  getToasts(): Array<RenderedToast> {
    return this.toasts;
  }

  _findToast(toastId: ToastId): {index: number; toast: RenderedToast} {
    // eslint-disable-next-line no-restricted-syntax
    const index = _.findIndex(this.toasts, {id: toastId});
    const toast = this.toasts[index];
    // Will return {index: -1, toast: undefined} if not found
    return {index, toast};
  }

  _getDefaultToastProps(toastType: ToastType): {isCloseable: boolean; duration: number} {
    return {
      isCloseable: true,
      // Let errors persist information since they often contain action for the user.
      duration: toastType === ToastType.DANGER ? FOREVER_DURATION : DEFAULT_DURATION,
    };
  }

  _setToastTimeout(toastId: ToastId): void {
    const {toast} = this._findToast(toastId);
    assert(Boolean(toast), 'Unexpected toastId', {toastId});
    if (toast.timeoutId != null) {
      clearTimeout(toast.timeoutId);
    }
    if (toast.duration === 0) {
      this._updateToast(toastId, {timeoutId: null});
      return;
    }
    const duration = toast.duration ?? DEFAULT_DURATION;
    const timeoutId = setTimeout(ToastActions.closeToast.bind(null, toastId), duration * 1000);
    this._updateToast(toastId, {timeoutId});
  }

  _updateToast(toastId: ToastId, fieldsToMerge: Partial<RenderedToast>): void {
    const {index, toast} = this._findToast(toastId);
    assert(Boolean(toast), 'Unexpected toastId', {toastId});
    this.toasts = reactUpdate(this.toasts, {
      [index]: {
        $merge: fieldsToMerge,
      },
    });
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.TOAST_PUSH: {
        const {
          id,
          actions,
          actionText,
          clipboardData,
          duration,
          isCloseable,
          message,
          onActionClick,
          onClose,
          progress,
          skipCloseCallbackOnActions,
          type,
          stack,
        } = action;

        const {toast} = this._findToast(id);
        const toastProps = selectDefined({
          id,
          actions,
          actionText,
          clipboardData,
          duration,
          isCloseable,
          message,
          onActionClick,
          onClose,
          progress,
          skipCloseCallbackOnActions,
          type,
        });
        if (toast) {
          // Update existing toast
          if (typeof toastProps.duration === 'undefined') {
            toastProps.duration = DEFAULT_DURATION;
          }
          this._updateToast(id, toastProps);
          // Set new timeout for toast
          this._setToastTimeout(id);
        } else {
          // Add a new toast
          const newToast: RenderedToast = {...this._getDefaultToastProps(type), ...toastProps};
          this.toasts = stack ? [newToast, ...this.toasts] : [...this.toasts, newToast];
          this._setToastTimeout(newToast.id);
        }
        this.emitChange();
        break;
      }

      case EventConstants.TOAST_CLOSE: {
        const {index, toast} = this._findToast(action.id);
        if (toast) {
          this.toasts = reactUpdate(this.toasts, {$splice: [[index, 1]]});
          this.emitChange();
        }
        break;
      }

      default:
        break;
    }
    return true;
  }
}

const toastStore = new ToastStore(AppDispatcher);
export default toastStore;
